import { useEffect, useRef, useState } from 'react';
import { DateCardField, ModalFooter, PaneHeader, FilterControl, Icon, saveBlockers, useToast, useShellDismiss, useSavedDismiss, writeWithToast, CareTile, useKeepOpen, useDirtyDraft } from '../common';
import { api, ApiError } from '../../lib/api';
import { allSettledOrThrow } from '../../lib/allSettledOrThrow';
import { DOG_AREA_OPTIONS, KENNEL_AREAS, STATUS_PICK_OPTIONS } from './constants';
import { applyChip, gateRoster, seedDogAreas, unplacedBlocker, unsizedBlocker, type DogAreaMap } from './dogAreas';
import { Avatar } from '../common/Avatar';
import type { LedgerField } from './domain';
import { amountDue, clearsRecordedPayment, kindForNewPayment, ledgerFields, ledgerSignature, partitionMoneyEdits, replayKeyFor, reseedMoney, stayTotal, voidedPayments, nightsBetween, periodOf, statusForDeposit, ratePerNightToPersist, perNightForDogs } from './domain';
import { fmtDate } from '../../lib/dates';
import { fmtEuro, fmtEuroExact, parseMoneyText } from '../../lib/money';
import { deriveBookingFlags, type BookingFlag, type CarriedNote } from './flags';
import { CarePicker, type CarePickerProps } from './CarePicker';
import { DogMark } from './carePicked';
import { initialSelection } from './careSelection';
import type { BookingDetail, Owner, BookingStatus } from './types';

// One care-flag tile (staff-food-at-house): a chunky checkbox with an icon
// disc, title + one-line meaning, and a square tick. Tone classes: '' (brand),
// 'meds' (purple), 'mix' (terracotta) — colours all come from tokens.
/**
 * A money write that failed, carrying WHICH figure and WHAT was being done to it.
 *
 * The save used to track that in two variables it reassigned as it went. That
 * only worked while the writes were queued; they now go together, so a shared
 * cursor would report whichever field started last rather than the one that
 * broke. The error knows what it is.
 */
class MoneyEditError extends Error {
  constructor(readonly field: string, readonly verb: 'recorded' | 'cleared', readonly reason: unknown) {
    super(`payment write failed: ${field}`);
    this.name = 'MoneyEditError';
  }
}

// The household's nightly rate for an area — single price per booking, no per-dog
// split (every booking is total = rate × nights).
const areaRate = (area: string) => KENNEL_AREAS.find((a) => a.key === area)?.rate ?? KENNEL_AREAS[0]!.rate;

// One line of "Name (Breed)" for the pane header sub.
function dogsLine(owner: Owner): string {
  if (!owner.dogs.length) return 'No dogs on file';
  return owner.dogs.map((d) => (d.breed ? `${d.name} (${d.breed})` : d.name)).join(', ');
}

export interface BookingFormProps {
  booking: BookingDetail | null; // null = new booking
  owner: Owner;
  // For a NEW booking: the owner's most recent prior note (computed from the
  // timeline). An existing booking reads its inherited note from booking.carriedNote
  // instead. Shown as a read-only "carried from" block, never copied (booking-flags AC7).
  carriedNote?: CarriedNote | null;
  today: string; // ISO
  // `stay` — the keep-open tick was on: saved, and the modal is not to close.
  onSaved: (opts?: { stay?: boolean; createdId?: string }) => void;
  // THE NEW BOOKING'S ID, for a caller that has to record it somewhere — today
  // only the viewing conversion, which writes it to `viewings.booking_id`.
  //
  // OPT-IN, and it costs something. Writes default to `Prefer: return=minimal`
  // app-wide because no call site read a write's result, and every write route
  // rebuilds the full record to return it — a booking detail is eight round
  // trips at ~37ms each. Passing this asks for the record back, so the ~300ms
  // is paid ONLY on the path that genuinely needs the id, and every other
  // create is untouched.
  onCreated?: (bookingId: string) => void;
  onClose: () => void;
  // Clicking the header owner avatar opens the edit-owner pane.
  onEditOwner?: () => void;
  /** The booking's status, owned by the modal because the modal's bar draws it. */
  status: BookingStatus;
  onStatusChange: (s: BookingStatus) => void;
  /** Live flags, for the bar to draw — see `flagSig` above. */
  onFlagsChange?: (flags: BookingFlag[]) => void;
}

// The booking pane (edit + new). Pricing is a single household rate per night;
// status/deposit logic reuses format.ts and the save payload matches the API.
export function BookingForm({ booking, owner, carriedNote, today, onSaved, onCreated, onClose, onEditOwner, status, onStatusChange, onFlagsChange }: BookingFormProps) {
  // The shell owns the drawer's exit; `onClose` on its own unmounts the modal
  // where it stands. Cancel, a completed save and a delete all leave the way the
  // X does — one drawer, one exit.
  const close = useShellDismiss(onClose);
  // The close after a commit — Save's close-first, Delete — releases the guard.
  const closeSaved = useSavedDismiss(onClose);
  // One committed write per sheet — see `save`.
  const writing = useRef(false);
  const isNew = !booking;
  const dogIds = owner.dogs.map((d) => d.id);
  // Per-dog pricing reads each dog's size; the booking books the whole roster.
  const dogSizes = owner.dogs.map((d) => d.size as string | null);

  const [area, setArea] = useState<string>(booking?.area ?? 'little');
  // WHERE EACH DOG GOES (dogs-in-areas AC1). Seeded from what is stored — a
  // historical booking may hold NULLs, which is AC3's residue and exactly what
  // AC11 refuses to save over. A new booking starts every dog on the chip.
  const [dogAreas, setDogAreas] = useState<DogAreaMap>(() =>
    seedDogAreas(owner.dogs, booking, booking?.area ?? 'little'),
  );

  // The rules live in ./dogAreas so they can be tested — this suite renders with
  // renderToStaticMarkup and cannot click a chip.
  const pickArea = (next: string) => {
    setArea(next);
    setDogAreas((m) => applyChip(owner.dogs, next, m));
  };
  const [checkIn, setCheckIn] = useState(booking?.checkIn ?? '');
  const [checkOut, setCheckOut] = useState(booking?.checkOut ?? '');
  // periodOf maps legacy clock-time values (e.g. '17:00') to AM/PM, not just literal 'am'/'pm'.
  const [drop, setDrop] = useState(booking ? (periodOf(booking.dropOff) === 'PM' ? 'pm' : 'am') : 'am');
  const [coll, setColl] = useState(booking ? (periodOf(booking.collection) === 'PM' ? 'pm' : 'am') : 'pm');
  // The ledger as the modal shows it: ONE FIELD PER PAYMENT — deposit, payment
  // 2…, remainder. Summing everything after the deposit hid real transactions
  // (Freddi, 2026-07-31): a deposit, a part-payment that did not settle, then a
  // desk settlement read as two figures instead of three. See ledgerFields.
  //
  // A NEW booking has no ledger to show — its opening deposit travels with the
  // create — so it renders the first field only.
  const fields = isNew ? [{ paymentId: null, label: 'Deposit paid', amount: 0, kind: 'deposit' as const }]
    : ledgerFields(booking?.payments);
  // Each field's text, seeded once. The modal is keyed by booking id, so a
  // switch to a different stay remounts rather than reseeding mid-edit.
  // Amounts and their touched flags move TOGETHER, in one state object, so the
  // re-seed below can read both from the same functional update — reading
  // `touched` out of the effect's closure would be a render behind.
  const [money, setMoney] = useState(() => reseedMoney({ keys: [], amounts: [], touched: [] }, fields));
  const { amounts, touched } = money;
  // `touched` per field tells a genuine edit from an untouched form, so merely
  // opening a booking and saving a note can never write to the ledger. No date is
  // asked for — a payment is dated the day it is entered.

  const setAmountAt = (i: number, v: string) => {
    setMoney((prev) => ({
      ...prev,
      amounts: prev.amounts.map((x, j) => (j === i ? v : x)),
      touched: prev.touched.map((x, j) => (j === i ? true : x)),
    }));
  };
  // RE-SEED when the ledger moves underneath an open modal — marking a stay paid
  // on the Daily desk has to show here at once. Scoped to the money fields on
  // purpose: remounting the whole form (a wider React key) would also discard an
  // unsaved note or date, since every field is seeded from the prop in a
  // `useState` initialiser. The signature is stable while the ledger is, so an
  // ordinary re-render leaves a half-typed figure alone.
  const signature = ledgerSignature(booking?.payments);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setMoney((prev) => reseedMoney(prev, fields));
  }, [signature]);
  // Inherited note (booking-flags AC7): the carried note TEXT is pasted into the
  // editable notes field; the banner above states only where it came from. The
  // booking's own note wins; otherwise the inherited note seeds the field. The
  // carried note can arrive after mount (a new booking awaits the owner timeline),
  // so an effect seeds the field when it is still pristine.
  const carried = booking?.carriedNote ?? carriedNote ?? null;
  const carriedText = carried?.text ?? '';
  const hasOwnNote = (booking?.notes ?? '').trim() !== '';
  const [notes, setNotes] = useState(booking?.notes ?? '');
  const [notesTouched, setNotesTouched] = useState(false);
  // Care flags (staff-food-at-house): the checkboxes are the SOURCE — each
  // controls whether this booking's dogs appear in the matching staff card.
  const [foodAtHouse, setFoodAtHouse] = useState(booking?.foodAtHouse ?? false);
  const [medsFlag, setMedsFlag] = useState(booking?.meds ?? false);
  // A new booking for a legacy do-not-mix household opens with Keep apart
  // pre-ticked — the household checkbox is retired (Freddi 2026-07-17), so
  // the standing instruction carries forward through this seed instead.
  const [keepApart, setKeepApart] = useState(booking ? booking.mixOk === false : (owner.doNotMix ?? false));
  /* WHICH DOGS EACH FLAG COVERS (per-dog-care-flags). Seeded from the booking's
     own join where it has one; a booking taken before 0034 has none, and the
     first tick seeds it from what the household record already knows. */
  /* THE INSTRUCTIONS ARE THE DOG'S AND THERE IS ONE OF THEM, so this holds only
     what the desk has CHANGED in this session — the field falls back to
     `dog.meds` for everything untouched. Saving writes them to the dogs, not to
     the booking, which is what keeps the household modal and this screen from
     ever disagreeing (decision: per-dog-care-flags-two-clocks). */
  const [dogMeds, setDogMeds] = useState<Record<string, string>>({});
  /* THE OTHER CLOCK'S EDITS: what the desk has changed, this session, on the
     STAY's copy after arrival. Falls back to the stamp for everything untouched. */
  const [stayMeds, setStayMeds] = useState<Record<string, string>>({});
  /* NEVER ON FOR NOBODY. The rows where any dog has answered; the same seed
     the first tick uses where none has — an old booking's legacy tick, or a
     new one the household flag lit (initialSelection). */
  const [foodDogs, setFoodDogs] = useState<string[]>(() => initialSelection('food', booking?.foodAtHouse ?? false, booking?.dogs, owner.dogs));
  const [medsDogs, setMedsDogs] = useState<string[]>(() => initialSelection('meds', booking?.meds ?? false, booking?.dogs, owner.dogs));
  const [apartDogs, setApartDogs] = useState<string[]>(() =>
    initialSelection('mix', booking ? booking.mixOk === false : (owner.doNotMix ?? false), booking?.dogs, owner.dogs),
  );
  /* THE TWO CLOCKS (decision: per-dog-care-flags-two-clocks). Until check-in
     the field is the dog's own record, editable; once the stay has begun it
     is what was stamped on arrival, read-only, because a household edit made
     in October must not rewrite what a dog was given in June. The stamp is
     the booking's SAVED state — a status flipped in this sheet stamps on Save. */
  // Use the pending choice, not the saved row: changing an arrived stay back
  // to pre-arrival and editing its instructions in the same Save must write the
  // dog's live record, not a snapshot that the pending transition clears.
  const checkedIn = !!booking && (status === 'arrived' || status === 'collected');
  /* A CUSTOMER'S OWN WORDS (Freddi, 2026-10-08: "meds notes submitted by
     customer should appear in booking at desk"). A request writes this stay's
     medication onto the booking's dogs before anyone has arrived (AC2). A
     SAVED booking still ahead of arrival can only hold a stay copy that way —
     a move back from arrival clears it — so that copy is the field, and the
     customer's avatar marks it. A pending move back in this sheet still reads
     the saved status, so it falls through to the dog's record as before. */
  const savedArrived = !!booking && (booking.status === 'arrived' || booking.status === 'collected');
  const ownCopy = (dogId: string) => booking?.dogs.find((d) => d.id === dogId) as ({ medsAtArrival?: string | null; medsBy?: number | null; apartBy?: number | null } | undefined);
  const customerCopy = (dogId: string) => !savedArrived && ownCopy(dogId)?.medsAtArrival != null;
  const stayCopy = (dogId: string) => checkedIn || customerCopy(dogId);
  const stampFor = (dogId: string): string | null =>
    stayCopy(dogId) ? (ownCopy(dogId)?.medsAtArrival ?? null) : null;
  // The customer's mark stays while the words are still theirs — gone as soon
  // as the desk types something else, saved or not.
  const stayMarkBy = (dogId: string): number | undefined => {
    const own = ownCopy(dogId);
    if (!stayCopy(dogId) || own?.medsBy == null) return undefined;
    const typed = stayMeds[dogId];
    return typed === undefined || typed === (own.medsAtArrival ?? '') ? own.medsBy : undefined;
  };
  // Seed the field with the inherited note when the booking has none of its own
  // and the user hasn't typed. Keyed on the carried TEXT (not object identity), so
  // switching to a different owner while composing a new booking re-seeds with the
  // new owner's note instead of keeping the previous one. (Codex P2)
  useEffect(() => {
    if (!hasOwnNote && !notesTouched) setNotes(carriedText);
  }, [carriedText, hasOwnNote, notesTouched]);
  /* STATUS LIVES IN THE MODAL NOW (Freddi, 2026-09-21: "they should all share
     the top bar"). It is drawn in the shell's bar beside the household, and a
     control cannot be drawn in one component while its state lives in another —
     so the state went up and this reads it as a prop. The deposit rule below
     still belongs here, because deposit is a field. */
  const setStatus = onStatusChange;
  /* A REQUEST READS, IT DOES NOT EDIT (decision 274): every field takes the
     locked treatment until she accepts — a field she can type into is an
     invitation to change a booking she has not accepted. A declined request
     stays locked as the record she left. The decision itself is the request
     card in the Self-service column; nothing about it is drawn here (Freddi,
     2026-10-10: "omit your request info in the middle section"). */
  const locked = !!booking && (booking.status === 'requested' || booking.status === 'declined');
  // Single household nightly rate (a free string so it can be fully cleared).
  const [rate, setRate] = useState<string>(() => {
    if (!booking) return String(perNightForDogs(dogSizes, areaRate(area)));
    if (booking.ratePerNight != null && booking.ratePerNight !== '') return String(parseFloat(booking.ratePerNight));
    const bn = nightsBetween(booking.checkIn, booking.checkOut);
    if (booking.total != null && bn > 0) return String(Math.round(parseFloat(booking.total) / bn));
    return String(areaRate(booking.area));
  });
  const [rateTouched, setRateTouched] = useState(false);
  // The unsaved-changes guard's one line: the fields a save writes; the
  // carried note is in only once typed.
  //
  // THE DRAFT IS ONLY WHAT THIS FORM OWNS AND ONLY A HAND CHANGES. Audited
  // 2026-10-08 after three false positives in a row: no setter of any field
  // below runs inside an effect. Three things do change under the baseline
  // without a hand on them, and each is handled by that rule rather than by
  // snapshotting it: money and the rate reseed when the Bookings page's slim
  // row hydrates into the full booking, and the carried note is pasted in once
  // the timeline loads — all three count only once TOUCHED, the flags the form
  // already keeps; and `status` is the modal's, set from the booking in an
  // effect after this form's first render, so the MODAL reports it against the
  // booking it holds (OwnerBookingModal) and it is not in this draft at all. A
  // derived flag snapshotted here was the third false positive.
  const { markSaved } = useDirtyDraft('booking', isNew ? 'this new booking' : 'this booking', {
    area, dogAreas, checkIn, checkOut, drop, coll,
    amounts: money.amounts.map((a, i) => (money.touched[i] ? a : null)),
    rate: rateTouched ? rate : null,
    notes: notesTouched ? notes : null, foodAtHouse, medsFlag, keepApart, dogMeds, stayMeds,
    foodDogs, medsDogs, apartDogs,
  });
  const keepOpen = useKeepOpen();
  const createKeyRef = useRef<string | null>(null);
  // Replay guards for the writes that APPEND — an open slot with no row behind
  // it yet. Edits to existing rows are absolute PATCHes and idempotent, so they
  // need no key.
  //
  // ONE KEY PER FIELD, not one per save. This was a single slot, which held only
  // while the money writes ran one at a time. They now run together, and two open
  // slots then shared a key — so the server discarded the second payment as a
  // replay and the save reported success with one figure missing. See
  // `replayKeyFor`.
  const appendKeysRef = useRef<Map<string, string>>(new Map());
  const toast = useToast();

  // The money fields parsed once. NULL means the text is not a complete figure
  // (someone mid-keystroke on "-"), which blocks Save rather than being read as
  // zero — reading it as zero would book the whole recorded amount away.
  const parsed = amounts.map((a) => parseMoneyText(a));
  // The figures to CALCULATE with. An incomplete field contributes nothing while
  // it is being typed; Save is disabled meanwhile, so nothing can be written off
  // the back of these.
  const values = parsed.map((v) => v ?? 0);
  // The deposit still drives the unconfirmed → confirmed promotion: money down is
  // what confirms a stay.
  const depositValue = values[0] ?? 0;

  /* A DEPOSIT CONFIRMS A BOOKING — and never the reverse (`statusForDeposit`
     only upgrades). WRITE ONLY WHEN IT WOULD CHANGE SOMETHING: with status now
     owned by the modal, an unconditional write here stamped whatever value this
     render had captured back over the modal's own, so a confirmed stay reverted
     to `unconfirmed` every time the money fields reseeded (2026-09-21). */
  useEffect(() => {
    const next = statusForDeposit(status, depositValue, booking?.source);
    if (next !== status) onStatusChange(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depositValue, status]);
  // On a NEW booking, the household rate auto-computes from the dogs (per-size
  // sum, −€5 for a sharing pair) and tracks the chosen area until the user types
  // their own — then it's theirs to set (and to clear).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (isNew && !rateTouched) setRate(String(perNightForDogs(dogSizes, areaRate(area)))); }, [area, isNew, rateTouched]);

  const hasDates = !!checkIn && !!checkOut;
  const n = hasDates ? nightsBetween(checkIn, checkOut) : null;
  const rateNum = parseFloat(rate) || 0;
  // The stay's price — see stayTotal. The stored total is the agreed figure and
  // is what the desk settles against, so it holds while the stay is unchanged;
  // an edited rate OR a change of dates re-prices it. Both halves matter: the
  // first stopped a settled booking reading as an overpayment, the second stops
  // an extended stay going partly unbilled.
  const datesChanged = !!booking && (checkIn !== booking.checkIn || checkOut !== booking.checkOut);
  const total = stayTotal({
    isNew,
    rateTouched,
    datesChanged,
    storedTotal: booking?.total != null && booking.total !== '' ? Math.round(parseFloat(booking.total)) : null,
    nights: n,
    rate: rateNum,
  });
  // MONEY RECEIVED is EVERY figure on show, live, so the balance moves as any of
  // them is typed. What differs is only how they are PERSISTED: the deposit
  // travels with the create on a new booking; on an existing one each field
  // writes its own payment row.
  const received = Math.round(values.reduce((a, b) => a + b, 0) * 100) / 100;
  const balance = amountDue({ total, deposit: received });
  // OVERPAYMENT is a real state, not an error: more was collected than the stay
  // cost, and the difference is owed BACK. The balance line used to hard-code
  // "€0" whenever the booking read as paid, which made that money disappear from
  // the modal entirely. Show it, and name it correctly — "Balance due −€50" is
  // not something anyone should have to decode.
  // Guarded on hasDates, NOT on `total > 0`. A stay with no dates has no price
  // yet, so a deposit typed against it must not be announced as money owed back
  // on a booking nobody has costed. But a PRICED stay that genuinely costs
  // nothing and took money really does owe it back — and there are €0 bookings
  // in the real data — so `total > 0` would silence a true credit while fixing
  // the false one.
  const credit = hasDates && balance < 0;
  // The Paid stamp is a READOUT now, not a verdict this form computes and
  // persists: payment_status is derived server-side from the ledger
  // (apps/api/src/payments/ledger.ts), so the client asserting one could only
  // ever disagree with it.
  const paid = received > 0 && total > 0 && balance <= 0;
  // Payments already cleared, shown as history under the fields.
  const cleared = voidedPayments(booking?.payments);
  // Why Save cannot be pressed, in the words to show. A half-typed money field is
  // the only money case left here: the figure it would write is meaningless, and
  // it is money.
  const blockers = saveBlockers(
    dogIds.length === 0 && 'This household has no dogs on file.',
    !hasDates && 'Choose the check-in and check-out dates.',
    hasDates && checkOut < checkIn && 'Check-out cannot fall before check-in.',
    parsed.some((v) => v === null) && 'One of the payment figures is not a complete amount.',
    // AC11 — a save is refused while any dog is unplaced, and the refusal NAMES
    // the dog. "Something is wrong" is not actionable in a modal.
    //
    // SCOPED TO STAYS THAT HAVE NOT ENDED (Freddi, 2026-09-11: "it doesnt really
    // matter about ones in the past"). 125 historical `mix` bookings land with
    // their dogs unplaced and nobody alive can say where those dogs slept in
    // 2024 — demanding an answer would make every one of them hostile to edit,
    // so Patricia could not fix a typo in a note without inventing a room.
    // Three upcoming bookings need the answer, and she knows those.
    (!checkOut || checkOut >= today) && unplacedBlocker(owner.dogs, area, dogAreas),

  );
  const canSave = blockers.length === 0;
  // At-a-glance flags (booking-flags AC3), derived from the LIVE edit state in
  // BOTH modes — edit overlays the live fields on the saved row; create builds
  // from the owner's dogs and the form fields. Ticking a care tile (or typing a
  // note) shows its chip in the strip immediately, new booking or not.
  const liveFlagFields = {
    foodAtHouse, meds: medsFlag, mixOk: !keepApart, notes,
    checkIn: checkIn || today, carriedNote: carried,
    // THE STRIP READS THE SELECTION AS SHE TICKS IT. A dog unticked this
    // stay loses its chip at once; one ticked with no words gains one.
    dogs: (booking?.dogs ?? owner.dogs).map((d) => ({
      ...d,
      needsMeds: medsFlag ? medsDogs.includes(d.id) : false,
      medsAtArrival: stampFor(d.id),
    })),
  };
  const detailFlags = deriveBookingFlags(booking ? { ...booking, ...liveFlagFields } : liveFlagFields);
  // First-stay moved beside the title (the table's New indicator); the strip
  // carries the rest.
  const stripFlags = detailFlags.filter((f) => f.kind !== 'first-booking');
  /* THE BAR DRAWS THESE, SO THE BAR HAS TO BE TOLD. They recompute as she ticks
     a care tile or types a note, and that liveness is the point — a strip that
     only caught up on Save would be describing a booking that no longer exists
     on screen. Reported on a SIGNATURE rather than on the array: a fresh array
     every render would loop. */
  const flagSig = stripFlags.map((f) => `${f.kind}:${f.dogId ?? ''}:${f.label}`).join('|');
  useEffect(() => {
    onFlagsChange?.(stripFlags);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flagSig]);

  /**
   * Persist ONE touched money field, and label its own failure.
   *
   * Extracted so the edits can be issued together — a loop body cannot be, and
   * inlining it into the `allSettledOrThrow` call would have buried thirty lines of
   * reasoning inside a `.map`.
   */
  const writeMoneyEdit = async (
    bookingId: string,
    e: { field: LedgerField; value: number },
  ): Promise<void> => {
    const verb = clearsRecordedPayment(e.field, e.value) ? 'cleared' : 'recorded';
    try {
      if (e.field.paymentId) {
        // An amount of ZERO is read by the endpoint as "clear this row" and
        // answered by voiding it — the row stays on the ledger with its amount
        // and its date, marked with the day it was cleared and by whom. One call
        // either way: the PATCH is absolute, so both the correction and the clear
        // are idempotent and need no replay key.
        await api(`/bookings/${bookingId}/payments/${e.field.paymentId}`, {
          method: 'PATCH', body: { amount: e.value },
        });
      } else if (e.value !== 0) {
        // A field with no row behind it yet. This one APPENDS, so it carries a
        // replay key: two identical appends would otherwise be two real payments.
        // The key belongs to THIS FIELD — a booking with nothing recorded offers
        // two open slots, and sharing one key between them makes the server
        // discard the second payment as a replay (see replayKeyFor).
        //
        // Its KIND follows the money, not the label. A partial payment typed here
        // must NOT claim the settling position, or the desk's Paid tap finds
        // nothing outstanding, writes no row, and the third payment can never
        // exist — which is exactly what stopped "Payment 2" ever appearing.
        await api(`/bookings/${bookingId}/payments`, {
          method: 'POST',
          body: {
            amount: e.value,
            receivedOn: today,
            // By the SLOT it was entered in, not by a display label — see
            // kindForNewPayment for why the two ends differ.
            kind: kindForNewPayment(e.field.kind, received, total),
            clientKey: replayKeyFor(appendKeysRef.current, e.field, () => crypto.randomUUID()),
          },
        });
      }
    } catch (err) {
      // A 401 IS NOT A PAYMENT FAILURE. `api()` has already redirected to login,
      // and writeWithToast suppresses the error toast for exactly that reason —
      // but only if it can still SEE the 401. Wrapping it would put a
      // "the deposit could not be recorded" toast and a failure buzz over the
      // bounce to the login page, about a write that was never attempted.
      if (err instanceof ApiError && err.status === 401) throw err;
      throw new MoneyEditError(e.field.label.toLowerCase(), verb, err);
    }
  };

  const save = () => {
    if (writing.current) return;
    // Capacity counts SIZE and nothing else, so a dog with no size is invisible
    // to every availability sum — and the server refuses the write regardless.
    // Raised as a TOAST rather than a footer blocker (Freddi, 2026-09-11): the
    // fix is on the DOG, not on this form, so a permanently-disabled Save with
    // an explanation in the chrome tells Patricia she is stuck without telling
    // her where to go. The toast fires when she asks to save, and names them.
    // Only on a crossing INTO the gate — a new booking at a gated status, or a
    // request or cancellation moving to one. A booking already inside saves
    // freely (dog-size-gate-everywhere AC3b). One prefix, here; the sentence
    // is the server's.
    // The BOOKING's roster on an edit, the household's on a create — the
    // server checks the booking's own rows, and a never-booked unsized dog on
    // the household must not stop a reinstatement it would allow (review).
    const unsized = unsizedBlocker(gateRoster(booking, owner), { from: booking?.status ?? null, to: status });
    if (unsized) {
      toast.error(`Unable to save — ${unsized}`);
      return;
    }
    if (!canSave) return;
    // The sheet is no longer torn out on the first tap — it plays its 260ms exit
    // and stays mounted and interactive throughout, so a second tap or a held
    // Enter could start a second write. One write per sheet.
    //
    // The append keys are the second guard behind this one: a field's key now
    // survives for the life of the form (see `replayKeyFor`), so even a write
    // that slipped past this latch would replay rather than append twice.
    writing.current = true;
    // Only persist a REAL nightly rate — never a value merely derived from
    // total ÷ nights for a legacy row (that would invent a rate on a notes/status
    // edit and drift from the original total). See ratePerNightToPersist.
    const hadStoredRate = !!booking && booking.ratePerNight != null && booking.ratePerNight !== '';
    const rateToSave = ratePerNightToPersist({ isNew, rateTouched, hadStoredRate, rateNum });
    // Single household price → no per-dog overrides (dogRates empty).
    //
    // Neither `deposit` nor `paymentStatus` is sent on an EDIT: both are derived
    // from the payments ledger server-side. On a CREATE the opening deposit
    // still travels with the booking, so the two land in one transaction —
    // `depositReceivedOn` is the date it arrived, which is today, the day it is
    // being entered (the field for it was dropped: staff were being asked for a
    // date they had just implicitly given).
    const payload = {
      ownerId: owner.id, dogIds, area, dogAreas, checkIn, checkOut, dropOff: drop, collection: coll,
      total, ratePerNight: rateToSave, notes,
      mixOk: !keepApart, foodAtHouse, meds: medsFlag, status, dogRates: {},
      // WHICH DOGS EACH TILE COVERS (per-dog-care-flags AC20). An off tile
      // sends an empty list, so every row gets an explicit answer on Save.
      careDogs: { food: foodAtHouse ? foodDogs : [], meds: medsFlag ? medsDogs : [], mix: keepApart ? apartDogs : [] },
      // Only the instructions she actually typed on, and only while the stay
      // is ahead of us — the field is read-only after arrival (AC13, AC15).
      ...(!checkedIn && Object.keys(dogMeds).length ? { dogMeds } : {}),
      // After arrival the field is the stay's own copy (AC15, Freddi
      // 2026-10-06): edits go to booking_dogs, never to the record.
      ...(Object.keys(stayMeds).length && Object.keys(stayMeds).every((id) => stayCopy(id)) ? { stayMeds } : {}),
      ...(isNew ? { deposit: depositValue, depositReceivedOn: today } : {}),
    };
    // Every field the user actually TOUCHED, paired with the payment it edits.
    // Untouched fields never write, so opening a booking and saving a note can
    // not disturb the ledger.
    const moneyEdits = booking
      ? fields.map((f, i) => ({ field: f, value: values[i] ?? 0, touched: touched[i] ?? false }))
          .filter((e) => e.touched)
      : [];
    // Close instantly; the save + revalidation run in the background. The toast
    // provider and the SWR cache live above the modal, so they outlive the close.
    // KEEP OPEN (G14): with the tick on, the modal does not close first or
    // after; the host is told `stay`, revalidates in place and shows the
    // Saved mark. A create with the tick on lands on the booking it made.
    const stay = keepOpen.on;
    // A kept-open create lands on the booking it made (review on #239): the
    // id comes back with the create and travels to the host, which opens it.
    // Without it the form stayed in `new` and a second Save created a twin.
    let createdId: string | undefined;
    void writeWithToast(toast, {
      closeFirst: stay ? undefined : closeSaved,
      write: async () => {
        if (booking) {
          await api(`/bookings/${booking.id}`, { method: 'PATCH', body: payload });
          // TWO LANES, decided by partitionMoneyEdits (PR #161 review).
          //
          // PATCHes go together: each writes an ABSOLUTE amount to ITS OWN row,
          // so no two touch the same row and none reads what another wrote.
          // Every one is awaited before a failure surfaces (allSettledOrThrow) —
          // reporting early would invite a reopen-and-retry while a sibling was
          // still in flight, appending the payment twice. A 401 among the
          // failures outranks the rest: an expired session voids the whole save
          // and every request 401s together, so the mixed case is the common one.
          //
          // APPENDS then run in FIELD ORDER, one at a time, and only once every
          // patch has landed. Their order IS the ledger — racing them let the
          // remainder take the earlier created_at, after which both figures read
          // back as numbered middle rows with the end slots open — and their
          // KIND is classified against the sum of every edited field, which is
          // only honest once that money has actually committed.
          //
          // All of it still follows the booking PATCH above: that one can move
          // `total`, and the recompute each payment write performs reads it.
          const { patches, appends } = partitionMoneyEdits(moneyEdits);
          await allSettledOrThrow(
            patches.map((e) => writeMoneyEdit(booking.id, e)),
            (err) => err instanceof ApiError && err.status === 401,
          );
          for (const e of appends) await writeMoneyEdit(booking.id, e);
        }
        else {
          createKeyRef.current ??= crypto.randomUUID();
          // Only ask for the record back when somebody is going to read it.
          const res = await api<{ id: string }>('/bookings', {
            method: 'POST',
            body: payload,
            idempotencyKey: createKeyRef.current,
            ...(onCreated || stay ? { wantResult: true } : {}),
          });
          createKeyRef.current = null;
          createdId = res?.data?.id;
          // AFTER the create has landed, never before — the id is written to the
          // viewing from here, and a speculative write would claim a booking
          // that does not exist (book-them-in AC8).
          if (onCreated && res?.data?.id) onCreated(res.data.id);
        }
      },
      success: booking ? 'Booking updated' : 'Booking created',
      onSaved: () => {
        if (stay) {
          markSaved();
          writing.current = false;
        }
        onSaved(stay ? { stay: true, createdId } : undefined);
      },
      // KEPT OPEN AND FAILED: the form is still mounted and the latch above is
      // still set, so without this every later Save returned at the latch —
      // one dropped connection and the booking was unsaveable until reopened
      // (review on #239). Closed-first, the form is gone and this is moot.
      onError: () => { writing.current = false; },
      // These writes are NOT atomic, so name the one that failed. "Could not save
      // the booking" after the PATCH already landed is a lie the user cannot see
      // through — the modal is closed, and the booking edit is sitting there
      // saved.
      //
      // The failure carries its OWN identity rather than the save tracking "which
      // one are we on" in a variable. It had to: the money writes now run
      // together, so a shared cursor would name whichever field happened to start
      // last, not the one that broke.
      error: (e) =>
        e instanceof MoneyEditError
          ? `The booking was saved, but the ${e.field} could not be ${e.verb}. Reopen the booking and try again.`
          // A validation refusal carries the sentence Patricia needs — which
          // dog has no size — and "Could not save the booking" buried it.
          : e instanceof ApiError && e.code === 'validation_error' && e.message
            ? `Unable to save — ${e.message}`
            : 'Could not save the booking.',
    });
  };
  const doDelete = () => {
    if (!booking || writing.current) return;
    writing.current = true;
    void writeWithToast(toast, {
      write: () => api(`/bookings/${booking.id}`, { method: 'DELETE' }),
      success: 'Booking deleted',
      error: 'Could not delete the booking.',
      // Unlike `save`, this has no `closeFirst` — the modal stays open when the
      // request fails, so a latch left set would disable Delete AND Save for the
      // life of the form, silently and with nothing on screen to explain it.
      onError: () => { writing.current = false; },
      onSaved: () => { onSaved(); closeSaved(); },
    });
  };

  const apSeg = (val: string, set: (v: string) => void) => (
    <div className="useg useg-sm">
      <button type="button" className={val === 'am' ? 'on' : ''} onClick={() => set('am')}>AM</button>
      <button type="button" className={val === 'pm' ? 'on' : ''} onClick={() => set('pm')}>PM</button>
    </div>
  );

  const ownerName = owner.contacts[0]?.name ?? 'Owner';
  /* THE DOGS ON THIS STAY, NOT THE HOUSEHOLD'S (PR #228 review, codex
     2026-10-06). The PATCH is set-based over the booking's own rows and
     inserts none, so a dog added to the household after a frozen booking
     could be ticked here, answer 200, and be gone on reopen — with typed
     instructions still reaching that dog's record. An existing booking offers
     its rows; a new one has no rows yet and offers the household, which is
     what POST seeds them from. */
  const roster = booking ? owner.dogs.filter((d) => booking.dogs.some((bd) => bd.id === d.id)) : owner.dogs;
  const foodPick: CarePickerProps = {
    kind: 'food', on: foodAtHouse, onToggle: setFoodAtHouse, dogs: roster,
    selected: foodDogs, onSelect: setFoodDogs,
    icon: 'bowl', title: 'Food at house', sub: "Owner's food stored at the house",
  };
  const medsPick: CarePickerProps = {
    kind: 'meds', on: medsFlag, onToggle: setMedsFlag, dogs: roster,
    selected: medsDogs, onSelect: setMedsDogs,
    icon: 'medication', title: 'Medication', sub: 'A dog on this stay needs meds',
    markFor: (d) => <DogMark by={stayCopy(d.id) ? stayMarkBy(d.id) : d.confirmedBy?.meds} owner={owner} />,
    instructions: (d) => {
      const stamp = stampFor(d.id);
      /* THE STAY'S OWN COPY, once it has begun (AC15). NEVER DISABLED and
         NOTHING OVER IT (Freddi, 2026-10-06: "we should always be able to edit
         instructions from our side"; "we should only show input and its
         contents"): the field simply IS the stay's copy, and edits go to it,
         not to the record. That the record may have moved on since arrival is
         the household modal's to show, not this field's. */
      /* CHOSEN BY THE CLOCK, NOT BY THE STAMP (PR #228 review, codex
         2026-10-06). This opened the stay's editor only where a stamp EXISTED,
         while the payload sends `stayMeds` whenever the stay is checked in — so
         a dog ticked AFTER arrival, stamp still null, was typed into `dogMeds`
         and the save dropped it. Checked in means the stay's copy, seeded from
         the record until the stamp lands. */
      if (stayCopy(d.id) && booking) {
        return (
          <textarea
            className="cp-input"
            rows={2}
            value={stayMeds[d.id] ?? stamp ?? d.meds ?? ''}
            placeholder={`What ${d.name} takes this stay`}
            onChange={(e) => setStayMeds((m) => ({ ...m, [d.id]: e.target.value }))}
          />
        );
      }
      /* A TEXTAREA, NOT AN INPUT. The column is ~190px and a dog's medication
         is a sentence: "Half a Loxicom tablet with breakfast" runs off the end
         of a single line and the desk cannot see what it typed. Two rows, and
         it wraps. */
      return (
        <textarea
          className="cp-input"
          rows={2}
          value={dogMeds[d.id] ?? d.meds ?? ''}
          placeholder={`What ${d.name} takes, and when`}
          onChange={(e) => setDogMeds((m) => ({ ...m, [d.id]: e.target.value }))}
        />
      );
    },
  };
  const apartPick: CarePickerProps = {
    kind: 'mix', on: keepApart, onToggle: setKeepApart, dogs: roster,
    selected: apartDogs, onSelect: setApartDogs,
    icon: 'do-not-mix', title: 'Keep apart', sub: 'Do not mix with other households',
    /* THE CUSTOMER'S OWN ASK FOR THIS STAY (0037) — a request names the dogs to
       keep apart, and marks them; the household's standing answer marks the
       rest. The same column as medication's mark: one place for every "they
       told us this" on the card. */
    markFor: (d) => <DogMark by={ownCopy(d.id)?.apartBy ?? d.confirmedBy?.keepApart} owner={owner} />,
  };

  const paneBody = (
    <>
      <div className="pane-grid">
        <div className="scard">
          <span className="sh">Stay</span>
          <DateCardField from={checkIn} to={checkOut} today={today} unit="night"
            onChange={(f, t) => { setCheckIn(f); setCheckOut(t); }} />
          <div className="row3" style={{ marginTop: 10 }}>
            <div className="times">
              <div><span className="ctl-label">Drop-off</span>{apSeg(drop, setDrop)}</div>
              <div><span className="ctl-label">Collection</span>{apSeg(coll, setColl)}</div>
            </div>
            <div>
              <span className="ctl-label">Area</span>
              <div className="chips area-chips">{KENNEL_AREAS.map((a) => <button key={a.key} type="button" className={'chip' + (area === a.key ? ' on' : '')} onClick={() => pickArea(a.key)}>{a.label}</button>)}</div>
              {/* AC2 — Other slides a row per dog down beneath the chips. AC12:
                  nothing appears here for Big, Little or House; the card reads
                  exactly as the desk has always drawn it. */}
              <div className={'perdog' + (area === 'other' ? ' open' : '')}>
                <div className="perdog-in">
                  <div className="perdog-h">Where each dog goes</div>
                  {owner.dogs.map((d) => (
                    <div key={d.id} className={'drow' + (dogAreas[d.id] ? '' : ' needs')}>
                      <span className="dname">
                        <Avatar name={d.name} id={d.id} kind="dog" size={28} />
                        <span className="nm">{d.name}{d.breed ? <i> {d.breed}</i> : null}</span>
                      </span>
                      <FilterControl
                        label="Area"
                        mode="single"
                        // CONDENSED: the pill shares this row with the dog's name
                        // and breed, and the name is the half that says which dog.
                        size="sm"
                        clearable={false}
                        options={DOG_AREA_OPTIONS}
                        selected={dogAreas[d.id] ? [dogAreas[d.id] as string] : []}
                        onChange={(keys) => {
                          if (keys[0]) setDogAreas((m) => ({ ...m, [d.id]: keys[0]! }));
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={'scard pricing-card' + (paid ? ' paid' : '')}>
          {paid && <div className="paid-stamp" aria-hidden="true">Paid</div>}
          <div className="sh-row"><span className="sh">Pricing</span></div>
          <div className="quote-row"><span className="label">Nights</span><span className="val">{n == null ? '—' : n}</span></div>
          <div className="quote-row">
            <span className="label">Rate per night · household</span>
            <span className="qrate"><span className="pf">€</span><input type="number" value={rate} onChange={(e) => { setRate(e.target.value); setRateTouched(true); }} onFocus={(e) => e.target.select()} /></span>
          </div>
          {/* ONE FIELD PER PAYMENT — deposit, payment 2…, remainder. Summing
              everything after the deposit hid real transactions: a deposit, a
              part-payment that did not settle, then a desk settlement showed as
              two figures instead of three. Labels are positional (see
              ledgerFields); a NEW booking renders only the first, since its
              opening deposit travels with the create.

              No date picker: a payment is dated the day it is entered.

              TEXT, not number. `<input type="number">` sanitises the intermediate
              "-" to "", so a controlled React input swallows the minus key and a
              negative figure — a refund, a credit, arrears carried forward — is
              literally untypeable. inputMode keeps the numeric keypad. The text
              is parsed by parseMoneyText, and an incomplete one blocks Save
              rather than being read as zero. */}
          {fields.map((f, i) => (
            <div className="quote-row" key={f.paymentId ?? `new-${i}`}>
              {/* NO "will be cleared" ANNOUNCEMENT (Freddi, 2026-08-15). Emptying
                  a figure is an ordinary edit and reads as one; narrating it back
                  made a routine correction feel like a warning. The record still
                  survives — the row is voided, not deleted, and shows below as
                  cleared history once saved. */}
              <span className="label">{f.label}</span>
              <span className={'qrate' + (parsed[i] === null ? ' invalid' : '')}>
                <span className="pf">€</span>
                <input type="text" inputMode="decimal" aria-label={f.label}
                  aria-invalid={parsed[i] === null}
                  value={amounts[i] ?? ''}
                  onChange={(e) => setAmountAt(i, e.target.value)}
                  onFocus={(e) => e.target.select()} />
              </span>
            </div>
          ))}
          {/* Payments already cleared. Shown, not hidden: a void nobody can see
              is indistinguishable from a silent deletion, and being able to see
              it is what makes clearing one a single press. */}
          {cleared.length > 0 && (
            <div className="pay-cleared">
              {cleared.map((p) => (
                <div className="pay-cleared-row" key={p.id}>
                  <span className="amt">{fmtEuroExact(p.amount)}</span>
                  {/* The WHOLE timestamp, not its date half. `voided_at` is UTC,
                      and slicing it off renders the UTC calendar day — so a void
                      at 23:30 on the 15th reads as the 15th to a kennel that was
                      already into the 16th. fmtDate localises it. */}
                  {/* NOT `when` — `.obx .when` is an existing global carrying a
                      14px bottom margin, which this row would have inherited. */}
                  <span className="pay-cleared-when">cleared {fmtDate(p.voidedAt)}</span>
                </div>
              ))}
            </div>
          )}
          <div className="quote-row total"><span className="label">Total</span><span className="val">{fmtEuro(total)}</span></div>
          {/* Credit shows the MAGNITUDE under its own label: "Credit owed €50",
              never "Balance due −€50". */}
          <div className={'due-stat' + (credit ? ' credit' : '')}>
            <span className="l">{credit ? 'Credit owed' : 'Balance due'}</span>
            <span className="amt">{fmtEuro(credit ? -balance : balance)}</span>
          </div>
        </div>
      </div>

      {/* The payments ledger PANEL used to sit here. It is deliberately not
          mounted: the interaction was never signed off and it went out in a
          release meant to omit it. Everything behind it survives — the ledger
          tables, POST/DELETE /api/bookings/:id/payments, and `Received` above,
          which is still the sum of the ledger's rows. `PaymentsPanel` itself is
          untouched in ./PaymentsPanel and still under test; remount it here once
          the design is chosen. */}

      <div className="scard care-card">
        <span className="sh">Care &amp; notes</span>
        {/* THREE COLUMNS, each tile with its own panel directly beneath it
            (Freddi, 2026-09-21). A panel that spans the card gains width and
            loses the one thing it most needs to say — which tile opened it. */}
        <div className="care-tiles">
          <CarePicker {...foodPick} />
          <CarePicker {...medsPick} />
          <CarePicker {...apartPick} />
        </div>
        {carried && !hasOwnNote && (
          <div className="carried-note"><Icon name="undo" size={13} /> Carried from the booking on {fmtDate(carried.fromCheckIn)}</div>
        )}
        {/* THE NOTE IS A FIELD LIKE ANY OTHER (Freddi, 2026-10-08): labelled,
            and when a customer wrote it, their mark sits at the label's end —
            the place every desk field carries one. It goes the moment the desk
            types something else, saved or not. */}
        <div className="field field-notes">
          <label>
            Notes
            <DogMark by={booking?.notesBy != null && notes === (booking.notes ?? '') ? booking.notesBy : undefined} owner={owner} />
          </label>
          <textarea rows={3} value={notes} onChange={(e) => { setNotes(e.target.value); setNotesTouched(true); }} placeholder="Special requirements…" />
        </div>
      </div>
    </>
  );

  return (
    <div className={'pane anim-fade booking-pane' + (isNew ? ' creating' : '')}>
      {/* THE IDENTITY BAR — CHROME, NOT CONTENT (Freddi, 2026-09-19: "we
          basically moved all this out of the edit bookign form and up to the
          top bar of the modal").

          It used to be the first thing INSIDE `.pane-scroll`, so who the
          booking was for, what state it was in and what needed attention all
          slid out of sight the moment Patricia scrolled to the notes — which is
          exactly when she is likeliest to want them. They are not fields. They
          are the frame the fields sit in.

          `.pane` is already a flex column with `overflow:hidden` and
          `.pane-scroll` is already `flex:1; overflow:auto`, so lifting this out
          pins it with no positioning of its own — no sticky, no z-index, no
          height to keep in sync.

          THE ORDER IS WHAT SHE ASKS IN: who and which dog, then what state the
          booking is in, then what needs attention. Identity first because it is
          the only one that can be wrong without the screen telling her; the
          flags last because they are the loudest and would otherwise be read
          before the thing they qualify. */}
      <div className="pane-scroll">
      {locked ? <fieldset className="locked-fields" disabled>{paneBody}</fieldset> : paneBody}
      </div>

      <ModalFooter
        onCancel={close}
        onSave={save}
        saveLabel={isNew ? 'Create Booking' : 'Save'}
        blockers={locked ? [booking?.status === 'declined' ? 'This request was declined — there is nothing to save' : 'Accept or decline the request on the right'] : blockers}
        danger={booking ? { label: 'Delete', disabled: locked, disabledHint: 'Accept or decline the request first', confirm: { message: 'Delete this booking?', confirmLabel: 'Delete booking', onConfirm: () => void doDelete() } } : null}
        keepOpen={{ on: keepOpen.on, onChange: keepOpen.setOn }}
      />
    </div>
  );
}
